import type { Bill } from '../types/bill';
import type { PurchaseTransaction } from '../types/purchaseTransaction';
import { canEnterBillEditor, isCostCorrectionEdit, canCostCorrectBill } from './billCostCorrection';

const amountsEqual = (a: string, b: string) =>
  Math.abs(parseFloat(a) - parseFloat(b)) < 0.0001;

/** Editable when DRAFT, or OPEN received with no payments applied (balance = total_amount). */
export const canEditBill = (
  bill: Pick<Bill, 'status' | 'balance' | 'total_amount' | 'received_into_inventory'>
): boolean => {
  if (bill.status === 'DRAFT') return true;
  if (
    bill.status === 'OPEN' &&
    bill.received_into_inventory &&
    amountsEqual(bill.balance, bill.total_amount)
  ) {
    return true;
  }
  return false;
};

/** UI gate — includes ADMIN-only cost correction on consumed bills. */
export const canEditBillInUi = (
  bill: Pick<Bill, 'status' | 'balance' | 'total_amount' | 'received_into_inventory' | 'lines'>
): boolean => canEnterBillEditor(bill);

export const billEditorBlockedMessage = (
  bill: Pick<Bill, 'status' | 'balance' | 'total_amount' | 'received_into_inventory' | 'lines'>
): string | null => {
  if (!canEditBill(bill)) {
    return 'This bill cannot be edited. Only drafts and open unpaid received bills (no payments applied) can be amended.';
  }
  if (isCostCorrectionEdit(bill) && !canCostCorrectBill()) {
    return 'This bill has sold inventory. Only an admin can cost-correct it.';
  }
  return null;
};

/** Prefer backend actions; fallback when list row may lack updated actions. */
export const canEditPurchaseTransaction = (txn: PurchaseTransaction): boolean =>
  txn.available_actions.includes('edit') ||
  (txn.status === 'OPEN' && amountsEqual(txn.balance, txn.total_amount));
